import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { CalendarIcon } from '../../components/svgs/icons/CalendarIcon';
import { Button } from '../Button';
import { Calendar } from '../Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { styleSheet } from './styles';

interface IProps {
	ariaLabel: string;
	from: string;
	to: string;
	onChange: (from: string, to: string) => void;
	/** Calendars shown side by side. Two by default; one suits a narrow filter bar. */
	numberOfMonths?: 1 | 2;
	size?: 'sm';
}

const ISO_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'MMM D, YYYY';

const parse = (value: string): Date | undefined => {
	if (!value) {
		return undefined;
	}
	const parsed = moment(value, ISO_FORMAT, true);
	return parsed.isValid() ? parsed.toDate() : undefined;
};

const formatLabel = (from: string, to: string): string => {
	const fromDate = parse(from);
	const toDate = parse(to);
	if (fromDate && toDate) {
		return `${moment(fromDate).format(DISPLAY_FORMAT)} – ${moment(toDate).format(DISPLAY_FORMAT)}`;
	}
	if (fromDate) {
		return `From ${moment(fromDate).format(DISPLAY_FORMAT)}`;
	}
	if (toDate) {
		return `Until ${moment(toDate).format(DISPLAY_FORMAT)}`;
	}
	return 'Select date range';
};

export const DateRangePicker: React.FC<IProps> = ({ ariaLabel, from, to, numberOfMonths = 2, onChange, size }) => {
	const [open, setOpen] = React.useState(false);
	const fromDate = parse(from);
	const toDate = parse(to);
	const hasValue = Boolean(fromDate || toDate);

	const handleSelect = (range: { from?: Date; to?: Date } | undefined) => {
		const nextFrom = range?.from ? moment(range.from).format(ISO_FORMAT) : '';
		const nextTo = range?.to ? moment(range.to).format(ISO_FORMAT) : '';
		onChange(nextFrom, nextTo);
	};

	const handleClear = () => {
		onChange('', '');
	};

	return (
		<div className={css(styleSheet.triggerOuter)}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						type='button'
						variant='ghost'
						styles={[
							styleSheet.trigger,
							size === 'sm' ? styleSheet.triggerSm : undefined,
							hasValue ? styleSheet.triggerHasClear : undefined,
						]}
						aria-label={ariaLabel}
					>
						<CalendarIcon />
						<span className={css(styleSheet.triggerLabel, !hasValue ? styleSheet.triggerLabelMuted : undefined)}>
							{formatLabel(from, to)}
						</span>
					</Button>
				</PopoverTrigger>
				<PopoverContent align='start' styles={[styleSheet.popoverContent]}>
					<Calendar
						mode='range'
						numberOfMonths={numberOfMonths}
						selected={{ from: fromDate, to: toDate }}
						defaultMonth={fromDate ?? toDate ?? new Date()}
						onSelect={handleSelect}
						captionLayout='dropdown'
					/>
					<div className={css(styleSheet.footer)}>
						<Button type='button' variant='ghost' size='sm' onClick={() => onChange('', '')}>
							Clear
						</Button>
						<Button type='button' variant='primary' size='sm' onClick={() => setOpen(false)}>
							Done
						</Button>
					</div>
				</PopoverContent>
			</Popover>
			{hasValue ? (
				<Button
					variant='ghost'
					size='icon'
					type='button'
					aria-label='Clear date range'
					styles={[styleSheet.clear]}
					onClick={handleClear}
				>
					×
				</Button>
			) : null}
		</div>
	);
};
